import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ErrorDeApi } from '@nucleo/api/cliente'
import { useTrabajoSinGuardar } from '@nucleo/trabajoSinGuardar'
import { Boton } from '@ui/primitivas/Boton'
import { agregarFila, decidirFila, editarFila, obtenerMatriz, proponerFilas, validarFilasEnLote } from './clienteDeMatrices'
import { ConfirmacionDeDescarte, Subpaso } from './ComponentesDeEtapa'
import { CLASES_DE_NIVEL, describirProcedencia, ETIQUETA_DE_ESTADO, resumenDeEvaluacion } from './etiquetas'
import { FormularioDeFila } from './FormularioDeFila'
import { TarjetaDeFila } from './TarjetaDeFila'
import type { Catalogo, Fila, Matriz, Procedencia, Tarea } from './tipos'

type Filtro = 'por_revisar' | 'todas' | 'con_visto_bueno'

const FILTROS: { valor: Filtro; etiqueta: string }[] = [
  { valor: 'por_revisar', etiqueta: 'Por revisar' },
  { valor: 'con_visto_bueno', etiqueta: 'Con visto bueno' },
  { valor: 'todas', etiqueta: 'Todas' },
]

function pasaFiltro(f: Fila, filtro: Filtro): boolean {
  if (filtro === 'por_revisar') return f.estado === 'propuesta'
  if (filtro === 'con_visto_bueno') return f.estado === 'validada'
  return true
}

function claseDeEvaluacion(f: Fila): string {
  const e = f.evaluacion
  if (f.version_motor !== 'evaluacion-isp-v3') return 'bg-atencion-fondo text-atencion-texto'
  if (e.tipo === 'motor') return CLASES_DE_NIVEL[e.nivel]
  return e.consolidada ? 'bg-conforme-fondo text-conforme-texto' : 'bg-informativo-fondo text-informativo-texto'
}

/**
 * Etapa 3 · Matriz asistida. Al entrar, si hay tareas validadas sin filas,
 * Atalaya propone automáticamente (P1 de la Constitución). El profesional
 * valida fila por fila: visto bueno · editar · descartar. La tabla resumen
 * permite recorrer matrices largas sin perder de vista qué falta revisar.
 */
export function PasoMatriz(props: { matriz: Matriz; catalogo: Catalogo; alActualizar: (m: Matriz) => void }) {
  const navegar = useNavigate()
  const { matriz, catalogo } = props
  const [proponiendo, setProponiendo] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [aviso, setAviso] = useState<{ agregadas: number; procedencia: Procedencia; descartes: string[] } | null>(null)
  const [editando, setEditando] = useState<Fila | null>(null)
  const [descartando, setDescartando] = useState<Fila | null>(null)
  const [agregando, setAgregando] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [validandoLote, setValidandoLote] = useState(false)
  const [errorDeFormulario, setErrorDeFormulario] = useState<string | null>(null)
  const [filtro, setFiltro] = useState<Filtro>(() => (matriz.progreso.filas_propuestas > 0 ? 'por_revisar' : 'todas'))
  const propuestaPedida = useRef<string | null>(null)

  const tareas = matriz.tareas.filter((t) => t.estado === 'validada')
  const tareaDe = (id: string) => matriz.tareas.find((t) => t.id === id)
  const activas = matriz.filas.filter((f) => f.estado !== 'descartada')
  const descartadas = matriz.filas.filter((f) => f.estado === 'descartada')
  const pendientes = activas.filter((f) => f.estado === 'propuesta')
  const pendientesHistoricas = pendientes.filter((f) => f.version_motor !== catalogo.version_motor)
  const listo = matriz.bloqueos_de_confirmacion.length === 0

  useTrabajoSinGuardar(editando !== null || agregando, 'La fila que estás editando')

  // Si ya no quedan filas por revisar, el filtro «Por revisar» quedaría vacío: se pasa a «Todas».
  useEffect(() => {
    if (filtro === 'por_revisar' && pendientes.length === 0) setFiltro('todas')
  }, [filtro, pendientes.length])

  const refrescar = async () => props.alActualizar(await obtenerMatriz(matriz.id))

  const proponer = async () => {
    setProponiendo(true)
    setError(null)
    try {
      const r = await proponerFilas(matriz.id)
      props.alActualizar(r.matriz)
      setAviso({ agregadas: r.agregadas, procedencia: r.procedencia, descartes: r.descartes })
      if (r.agregadas > 0) setFiltro('por_revisar')
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No fue posible obtener la propuesta. Vuelve a intentarlo con «Proponer para las tareas sin filas».')
    } finally {
      setProponiendo(false)
    }
  }

  // Propuesta automática al entrar: una sola vez por conjunto de tareas sin filas.
  useEffect(() => {
    const clave = matriz.tareas
      .filter((t) => t.estado === 'validada')
      .map((t) => t.id)
      .sort()
      .join(',')
    if (matriz.progreso.tareas_sin_filas > 0 && propuestaPedida.current !== clave && !proponiendo) {
      propuestaPedida.current = clave
      void proponer()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matriz.id, matriz.progreso.tareas_sin_filas])

  const decidir = async (f: Fila, decision: 'validar' | 'descartar', motivo?: string) => {
    setError(null)
    try {
      await decidirFila(matriz.id, f.id, decision, motivo)
      await refrescar()
      setDescartando(null)
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No fue posible registrar la decisión.')
    }
  }

  const irAFila = (f: Fila) => {
    const destino = document.getElementById(`tarjeta-fila-${f.id}`)
    if (!destino) return
    destino.scrollIntoView({ behavior: 'smooth', block: 'start' })
    destino.classList.remove('sst-resaltado')
    void destino.offsetWidth
    destino.classList.add('sst-resaltado')
    destino.querySelector<HTMLElement>('button')?.focus({ preventScroll: true })
  }

  // Numeración estable R1…Rn en el orden de las tareas, independiente del filtro.
  const ordenadas: { fila: Fila; tarea: Tarea; numero: number }[] = []
  for (const t of tareas) for (const f of activas.filter((x) => x.tarea_id === t.id)) ordenadas.push({ fila: f, tarea: t, numero: ordenadas.length })
  const visibles = ordenadas.filter(({ fila }) => pasaFiltro(fila, filtro) || fila.id === editando?.id || fila.id === descartando?.id)
  const cuenta = (valor: Filtro) => activas.filter((f) => pasaFiltro(f, valor)).length

  return (
    <div className="grid gap-4">
      <div>
        <p className="cs-eyebrow">Etapa 3 · Matriz asistida</p>
        <h2 className="text-h2">Peligros, riesgos, evaluación y medidas</h2>
        <p className="max-w-[80ch] text-pequeno text-texto-secundario">
          Por cada tarea validada, Atalaya propone el peligro según el catálogo ISP, la evaluación (VEP = P × C para seguridad y emergencias; protocolo para higiénicos, psicosociales y músculo-esqueléticos) y medidas con prelación.
        </p>
      </div>

      {tareas.length === 0 ? (
        <div role="status" className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-atencion-fondo p-4 text-atencion-texto">
          <span>No hay tareas con visto bueno todavía. Vuelve al levantamiento y revisa las tareas propuestas.</span>
          <Boton variante="secundario" compacto alPulsar={() => navegar(`/matrices/${matriz.id}/levantamiento`)}>Ir al levantamiento</Boton>
        </div>
      ) : null}

      {proponiendo ? (
        <div className="atalaya-propuesta-cargando" role="status" aria-live="polite">
          <div className="atalaya-emblema" aria-hidden="true"><img src="/chilesst/chilesst-mark.svg" alt="" /></div>
          <p className="text-h3">Atalaya está identificando peligros y evaluando riesgos…</p>
          <p className="text-pequeno text-texto-secundario">{matriz.progreso.tareas_sin_filas} tarea(s) por cubrir. Nada queda oficial hasta tu visto bueno.</p>
        </div>
      ) : null}

      {error ? <p role="alert" className="rounded-md bg-critico-fondo p-3 text-critico-texto">{error}</p> : null}
      {activas.some((f) => f.version_motor !== catalogo.version_motor) ? (
        <p role="status" className="rounded-md bg-atencion-fondo p-3 text-pequeno text-atencion-texto">
          Esta matriz contiene evaluaciones anteriores. Edita y reevalúa cada fila con la guía ISP antes de validarla u oficializar una nueva revisión. Los valores previos se conservan sin conversión automática.
        </p>
      ) : null}
      {aviso ? (
        <div role="status" className="rounded-md bg-informativo-fondo p-3 text-pequeno text-informativo-texto">
          {aviso.agregadas ? `Atalaya propuso ${aviso.agregadas} fila(s). ` : 'No hubo filas nuevas. '}
          {aviso.procedencia.origen ? describirProcedencia(aviso.procedencia.origen === 'ia' ? 'ia' : 'reglas', aviso.procedencia) : ''}
          {aviso.descartes.length ? (
            <details className="mt-1">
              <summary className="text-pequeno">Se descartaron {aviso.descartes.length} elemento(s) por no cumplir el catálogo o las escalas</summary>
              <ul className="list-disc pl-4">{aviso.descartes.map((d, i) => <li key={i}>{d}</li>)}</ul>
            </details>
          ) : null}
        </div>
      ) : null}

      {matriz.progreso.tareas_sin_filas > 0 && !proponiendo ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-atencion/50 bg-atencion-fondo/40 p-3">
          <span className="text-pequeno">{matriz.progreso.tareas_sin_filas} tarea(s) con visto bueno aún sin peligros registrados.</span>
          <Boton variante="secundario" compacto alPulsar={() => void proponer()}>Proponer para las tareas sin filas</Boton>
        </div>
      ) : null}

      {activas.length ? (
        <Subpaso
          numero="3.1"
          titulo="Resumen de la matriz"
          detalle={`${activas.length} fila(s) activas · ${pendientes.length} por revisar · ${matriz.progreso.filas_validadas} con visto bueno. Elige una fila para revisarla.`}
          activo={false}
          acciones={(
            <div className="sst-filtro" role="group" aria-label="Mostrar filas">
              {FILTROS.map((f) => (
                <button key={f.valor} type="button" aria-pressed={filtro === f.valor} onClick={() => setFiltro(f.valor)}>
                  {f.etiqueta}<b>{cuenta(f.valor)}</b>
                </button>
              ))}
            </div>
          )}
        >
          {/* La región con foco permite desplazar la tabla con teclado (WCAG 2.1.1). */}
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
          <div className="sst-tabla-marco" role="region" aria-label="Resumen de filas de la matriz" tabIndex={0}>
            <table className="sst-tabla min-w-[880px]">
              <caption className="sr-only">Filas de la matriz: número, puesto y tarea, peligro, riesgo, evaluación, medidas y estado de revisión</caption>
              <thead>
                <tr>
                  <th scope="col" className="sst-col-fija">Fila</th>
                  <th scope="col">Puesto · tarea</th>
                  <th scope="col">Peligro</th>
                  <th scope="col">Riesgo específico</th>
                  <th scope="col">Evaluación</th>
                  <th scope="col" className="sst-num">Medidas</th>
                  <th scope="col">Estado</th>
                </tr>
              </thead>
              <tbody>
                {visibles.length === 0 ? (
                  <tr><td colSpan={7} className="text-texto-secundario">No hay filas en esta vista.</td></tr>
                ) : visibles.map(({ fila: f, tarea: t, numero }) => (
                  <tr key={f.id} data-estado={f.estado}>
                    <th scope="row" className="sst-col-fija">
                      <a href={`#tarjeta-fila-${f.id}`} onClick={(e) => { e.preventDefault(); irAFila(f) }} aria-label={`Revisar la fila ${numero + 1}`}>R{numero + 1}</a>
                    </th>
                    <td><strong>{t.puesto}</strong><br /><span className="text-texto-secundario">{t.tarea}</span></td>
                    <td>{f.peligro}</td>
                    <td>{f.riesgo}<br /><span className="text-texto-secundario">{f.clasificacion}</span></td>
                    <td><span className={`sst-nivel ${claseDeEvaluacion(f)}`}>{resumenDeEvaluacion(f)}</span></td>
                    <td className="sst-num">{f.medidas.length || <span className="text-atencion-texto">Sin medidas</span>}</td>
                    <td><span className={`cs-state ${f.estado === 'propuesta' ? 'cs-state--soon' : 'cs-state--live'}`}>{ETIQUETA_DE_ESTADO[f.estado]}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Subpaso>
      ) : null}

      {activas.length || agregando ? (
        <Subpaso
          numero="3.2"
          titulo="Revisa cada fila"
          detalle={pendientes.length ? `${pendientes.length} de ${activas.length} pendiente(s). Visto bueno, editar o descartar.` : 'Todas revisadas.'}
          activo={pendientes.length > 0}
          acciones={(
            <>
              <Boton variante="secundario" compacto alPulsar={() => { setAgregando(true); setEditando(null); setDescartando(null) }}>Agregar fila a mano</Boton>
              {pendientes.length && !pendientesHistoricas.length ? (
                <Boton
                  variante="secundario"
                  compacto
                  trabajando={validandoLote}
                  alPulsar={() => {
                    setValidandoLote(true)
                    validarFilasEnLote(matriz.id)
                      .then(refrescar)
                      .catch(() => setError('No fue posible validar en lote.'))
                      .finally(() => setValidandoLote(false))
                  }}
                >
                  Visto bueno a las {pendientes.length} pendientes
                </Boton>
              ) : null}
            </>
          )}
        >
          {filtro !== 'todas' ? <p className="text-pequeno text-texto-secundario">Mostrando «{FILTROS.find((f) => f.valor === filtro)?.etiqueta}». <button type="button" className="font-semibold text-accion underline" onClick={() => setFiltro('todas')}>Ver todas las filas</button></p> : null}

          {agregando ? (
            <FormularioDeFila
              catalogo={catalogo}
              tareas={tareas}
              inicial={null}
              trabajando={guardando}
              error={errorDeFormulario}
              alCancelar={() => setAgregando(false)}
              alGuardar={(entrada) => {
                setGuardando(true)
                setErrorDeFormulario(null)
                agregarFila(matriz.id, entrada)
                  .then(refrescar)
                  .then(() => setAgregando(false))
                  .catch((e: unknown) => setErrorDeFormulario(e instanceof ErrorDeApi ? e.message : 'No fue posible agregar la fila.'))
                  .finally(() => setGuardando(false))
              }}
            />
          ) : null}

          {tareas.map((t) => {
            const filas = visibles.filter((v) => v.tarea.id === t.id)
            if (!filas.length) return null
            return (
              <section key={t.id} className="grid gap-2" aria-label={`Tarea: ${t.tarea}`}>
                <h4 className="pt-2 text-h3">
                  <span className="text-texto-secundario">{t.puesto}:</span> {t.tarea}
                </h4>
                {filas.map(({ fila: f, numero: i }) => {
                  if (editando?.id === f.id) {
                    return (
                      <FormularioDeFila
                        key={f.id}
                        catalogo={catalogo}
                        tareas={tareas}
                        inicial={f}
                        trabajando={guardando}
                        error={errorDeFormulario}
                        alCancelar={() => setEditando(null)}
                        alGuardar={(entrada) => {
                          setGuardando(true)
                          setErrorDeFormulario(null)
                          editarFila(matriz.id, f.id, entrada)
                            .then(refrescar)
                            .then(() => setEditando(null))
                            .catch((e: unknown) => setErrorDeFormulario(e instanceof ErrorDeApi ? e.message : 'No fue posible guardar la fila.'))
                            .finally(() => setGuardando(false))
                        }}
                      />
                    )
                  }
                  if (descartando?.id === f.id) {
                    return <ConfirmacionDeDescarte key={f.id} que={`la fila R${i + 1} (${f.peligro})`} alCancelar={() => setDescartando(null)} alConfirmar={(motivo) => void decidir(f, 'descartar', motivo)} />
                  }
                  return (
                    <TarjetaDeFila
                      key={f.id}
                      fila={f}
                      tarea={t}
                      indice={i}
                      alVistoBueno={() => void decidir(f, 'validar')}
                      alEditar={() => { setEditando(f); setAgregando(false); setDescartando(null); setErrorDeFormulario(null) }}
                      alDescartar={() => { setDescartando(f); setEditando(null) }}
                      alRecuperar={() => void decidir(f, 'validar')}
                    />
                  )
                })}
              </section>
            )
          })}
        </Subpaso>
      ) : null}

      {descartadas.length ? (
        <details className="sst-tarjeta px-4">
          <summary className="text-pequeno font-semibold">Filas descartadas ({descartadas.length})</summary>
          <div className="grid gap-2 pb-3">
            {descartadas.map((f, i) => (
              <TarjetaDeFila key={f.id} fila={f} tarea={tareaDe(f.tarea_id)} indice={activas.length + i} alVistoBueno={() => undefined} alEditar={() => undefined} alDescartar={() => undefined} alRecuperar={() => void decidir(f, 'validar')} />
            ))}
          </div>
        </details>
      ) : null}

      <div className="sst-barra-cierre" role="region" aria-label="Cerrar la etapa 3">
        <div className="grid gap-1">
          <p id="estado-cierre-matriz" className={listo ? 'text-conforme-texto' : ''}>
            {listo ? `Todo listo: ${matriz.progreso.filas_validadas} fila(s) con visto bueno. Puedes oficializar la matriz.` : 'Para oficializar falta:'}
          </p>
          {!listo ? (
            <ul className="list-disc pl-5 text-pequeno">
              {matriz.bloqueos_de_confirmacion.map((b) => <li key={b.codigo}>{b.mensaje}</li>)}
            </ul>
          ) : null}
        </div>
        <Boton deshabilitado={!listo} describidoPor="estado-cierre-matriz" alPulsar={() => navegar(`/matrices/${matriz.id}/oficializar`)}>
          Ir a oficializar
        </Boton>
      </div>
    </div>
  )
}
